export const NOTIFICATION_LIFECYCLE_STORAGE_KEY = 'jtl-dashboard-notification-lifecycle-v1';

const MAX_LIFECYCLE_ENTRIES = 500;
export const getNotificationSeverityRank = (severity) => (
    severity === 'critical' ? 3
        : severity === 'important' ? 2
            : severity === 'warning' ? 1 : 0
);
const finiteOrNull = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

export const getAlertLifecycleKey = (scopeKey, alertId) => `${scopeKey}|${alertId}`;

export const getAlertInstanceKey = (scopeKey, alert, registry = {}) => {
    const lifecycleKey = getAlertLifecycleKey(scopeKey, alert.id);
    const record = registry[lifecycleKey];
    return `${lifecycleKey}|e${record?.episode || 1}|r${record?.revision || 1}`;
};

export const readNotificationLifecycle = () => {
    if (typeof window === 'undefined') return {};
    try {
        const parsed = JSON.parse(window.localStorage.getItem(NOTIFICATION_LIFECYCLE_STORAGE_KEY) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

export const writeNotificationLifecycle = (registry) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(NOTIFICATION_LIFECYCLE_STORAGE_KEY, JSON.stringify(registry || {}));
    } catch {
        // Ignore localStorage write failures.
    }
};

const getDirectionalWorsening = (alert, previousValue, currentValue) => {
    if (previousValue === null || currentValue === null) return 0;
    return alert.worseWhen === 'lower'
        ? previousValue - currentValue
        : currentValue - previousValue;
};

export const reconcileNotificationLifecycle = ({ registry = {}, scopeKey, alerts = [], now = new Date(), allowResolutions = true }) => {
    const next = { ...registry };
    const changedAt = now instanceof Date ? now.toISOString() : new Date(now).toISOString();
    const scopePrefix = `${scopeKey}|`;
    const activeKeys = new Set((alerts || []).map(alert => getAlertLifecycleKey(scopeKey, alert.id)));

    if (allowResolutions) {
        Object.entries(next).forEach(([key, record]) => {
            if (!key.startsWith(scopePrefix) || record?.status !== 'active' || activeKeys.has(key)) return;
            next[key] = { ...record, status: 'resolved', resolvedAt: changedAt, changedAt };
        });
    }

    (alerts || []).forEach(alert => {
        const key = getAlertLifecycleKey(scopeKey, alert.id);
        const existing = next[key];
        const currentValue = finiteOrNull(alert.signalValue);
        const previousValue = finiteOrNull(existing?.signalValue);
        const currentSeverity = String(alert.severity || 'info');
        const primaryKey = String(alert.primaryKey || '');
        const threshold = Math.max(0, Number(alert.realertDelta ?? 1));

        if (!existing || existing.status === 'resolved') {
            next[key] = {
                status: 'active',
                episode: existing ? Number(existing.episode || 1) + 1 : 1,
                revision: 1,
                severity: currentSeverity,
                signalValue: currentValue,
                primaryKey,
                firstSeenAt: changedAt,
                changedAt
            };
            return;
        }

        const directionalWorsening = getDirectionalWorsening(alert, previousValue, currentValue);
        const worsened = threshold === 0 ? directionalWorsening > 0 : directionalWorsening >= threshold;
        const improved = threshold === 0 ? directionalWorsening < 0 : directionalWorsening <= -threshold;
        const severityEscalated = getNotificationSeverityRank(currentSeverity) > getNotificationSeverityRank(existing.severity);
        const newPrimaryProblem = Boolean(primaryKey && existing.primaryKey && primaryKey !== existing.primaryKey && !improved);
        const shouldRevise = severityEscalated || worsened || newPrimaryProblem;

        next[key] = {
            ...existing,
            status: 'active',
            revision: Number(existing.revision || 1) + (shouldRevise ? 1 : 0),
            severity: currentSeverity,
            signalValue: currentValue,
            primaryKey,
            resolvedAt: null,
            changedAt: shouldRevise ? changedAt : existing.changedAt
        };
    });

    const entries = Object.entries(next)
        .sort(([, a], [, b]) => String(b?.changedAt || '').localeCompare(String(a?.changedAt || '')))
        .slice(0, MAX_LIFECYCLE_ENTRIES);
    return Object.fromEntries(entries);
};
